/**
 * F-70 job state → UI, PURE (no React, no Yjs, no fetch).
 *
 * The doc's `txJob` is the ONE source of truth for translation progress: the
 * server publishes it under its own origin, so every client renders the same
 * bar, a mid-job reload still shows it, and no client polls. That makes the
 * mapping from that state to what a user sees the load-bearing logic of this
 * feature — and it is all here, testable without mounting anything.
 *
 * Counter semantics come straight from the schema and are worth restating,
 * because getting them wrong is invisible until a real run:
 *  - `done` = translated + skipped + failed — every row the job has FINISHED
 *    DECIDING about, not rows written. Skips are normal (an empty source row,
 *    a C5 guard refusal on a human-owned target), so a written-only numerator
 *    would stall the bar short of `total` on a perfectly healthy run.
 *  - `failed` is a SUBSET of `done`.
 *  - `translated` vs `skipped` is deliberately NOT on the doc — that breakdown
 *    is the job API's response (`GET …/:jobId`), read once by whoever asks.
 */
import type { TranslationJobState, TranslationJobStatus } from "@kola/shared";

import type { TranslateColumnResult, TranslateEstimate } from "../../core/api.js";

/**
 * F-70's confirmation threshold ("~500 rows"). Compared against the estimate's
 * `rows` (the non-empty rows that actually cost tokens), never against the
 * job's `total` — a 10k-row sheet with 200 filled rows is a 200-row bill and
 * must not demand a confirmation for spend that will not happen.
 */
export const COST_CONFIRM_ROWS = 500;

/** Is this job still live? Everything else is terminal and stays on the doc. */
export function isJobRunning(job: TranslationJobState | undefined): boolean {
  return job?.status === "running";
}

/**
 * Is this job's target THIS column? Drives the toolbar's spinner and picks the
 * dialog's wording — not whether a run may start, which is a per-BOARD question
 * (`isJobRunning`): the doc holds one job per board and the server keys the
 * registry the same way, so a run on ANY column blocks a run on any other.
 */
export function isJobFor(
  job: TranslationJobState | undefined,
  sheetId: string,
  colId: string,
): boolean {
  return job !== undefined && job.sheetId === sheetId && job.targetColId === colId;
}

/** 0–100, clamped and integral — the bar's width. */
export function jobPercent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

/** What the progress panel renders. */
export interface JobView {
  percent: number;
  /** "1,240 of 10,000 rows · 3 failed" — the bar's label and its a11y name. */
  label: string;
  /** Headline: "Translating into Japanese…" / "Translation cancelled". */
  title: string;
  /** Only a running job can be cancelled (F-70: DELETE stops it). */
  cancellable: boolean;
  /** Terminal states stay on the doc so the outcome can be read. */
  terminal: boolean;
}

/**
 * Map job state + the target column's CURRENT name to the panel's contents.
 * The name is passed in rather than stored on the job: a column renamed
 * mid-run must not leave the bar naming the old one.
 */
export function jobView(job: TranslationJobState, columnName: string): JobView {
  const percent = jobPercent(job.done, job.total);
  const parts = [`${job.done.toLocaleString("en-US")} of ${job.total.toLocaleString("en-US")} rows`];
  if (job.failed > 0) parts.push(`${job.failed.toLocaleString("en-US")} failed`);
  return {
    percent,
    label: parts.join(" · "),
    title: jobTitle(job.status, columnName),
    cancellable: job.status === "running",
    terminal: job.status !== "running",
  };
}

function jobTitle(status: TranslationJobStatus, columnName: string): string {
  switch (status) {
    case "running":
      return `Translating into ${columnName}…`;
    case "done":
      return `Finished translating into ${columnName}`;
    case "cancelled":
      return `Translation into ${columnName} cancelled`;
    case "error":
      return `Translation into ${columnName} failed`;
  }
}

/**
 * Should THIS client toast the outcome?
 *
 * Only on a transition a client actually WATCHED (running → terminal). The
 * state stays on the doc after the job ends, so a user opening the board an
 * hour later observes a terminal state as their FIRST observation — toasting
 * that would announce someone else's finished run as if it just happened.
 * `previous === undefined` is exactly that first observation, and never toasts.
 */
export function shouldReportOutcome(
  previous: TranslationJobState | undefined,
  next: TranslationJobState | undefined,
): boolean {
  if (previous === undefined || next === undefined) return false;
  // A different job replacing this one is a new run, not this one's outcome.
  if (previous.jobId !== next.jobId) return false;
  return previous.status === "running" && next.status !== "running";
}

/**
 * The outcome toast from the DOC alone — the fallback when the job API's
 * breakdown is unavailable (the registry is in-memory, so a server restart
 * forgets a finished job and `GET …/:jobId` 404s).
 *
 * It can only say what the doc knows: `done`/`total`/`failed`. It never
 * pretends to a `translated` count it does not have.
 */
export function jobOutcomeMessage(job: TranslationJobState, columnName: string): string {
  const done = job.done.toLocaleString("en-US");
  const total = job.total.toLocaleString("en-US");
  switch (job.status) {
    case "cancelled":
      return `Translation into ${columnName} cancelled at ${done} of ${total} rows. Run it again to continue.`;
    case "error":
      return `Translation into ${columnName} failed${
        job.message === undefined ? "" : `: ${job.message}`
      }. Run it again to retry.`;
    case "done": {
      // A "done" job that did not reach `total` stopped early on purpose (the
      // run budget, or a cancel that landed as the last tick) — the short bar
      // IS the signal, so say what to do about it.
      const short = job.done < job.total;
      const failed = job.failed > 0 ? ` ${job.failed.toLocaleString("en-US")} failed.` : "";
      if (short) {
        return `Translated ${done} of ${total} rows into ${columnName}.${failed} Run it again to continue.`;
      }
      return `Finished translating ${total} rows into ${columnName}.${failed}${
        job.failed > 0 ? " Run it again to retry them." : ""
      }`;
    }
    case "running":
      // Not an outcome — `shouldReportOutcome` never selects it.
      return `Translating into ${columnName}…`;
  }
}

/**
 * F-70's cost guard: "require confirmation past a threshold (~500 rows)".
 * Small runs start immediately — a confirmation on a 12-row run is a click
 * that teaches users to click through confirmations.
 */
export function needsCostConfirm(estimate: TranslateEstimate): boolean {
  return estimate.rows > COST_CONFIRM_ROWS;
}

/**
 * Would the start route refuse this run outright (`too-many-rows`)?
 *
 * Checked BEFORE `needsCostConfirm`, and the order is the point. The estimate is
 * deliberately planned without the server's row cap, so it happily quotes a run
 * bigger than the cap — and the refusal then arrived as a toast AFTER the user
 * had read "Translate 10,000 rows … < $0.01" and clicked it. Refusing here means
 * the price is only ever quoted for a run that can actually happen.
 */
export function overRowCap(estimate: TranslateEstimate): boolean {
  return estimate.maxRows !== undefined && estimate.rows > estimate.maxRows;
}

/** Why the run is too big, with both numbers — the cap is env-configurable. */
export function overRowCapMessage(estimate: TranslateEstimate): string {
  const rows = estimate.rows.toLocaleString("en-US");
  const cap = (estimate.maxRows ?? 0).toLocaleString("en-US");
  return `This run needs ${rows} rows, but the server allows at most ${cap} in one run — ask an admin to raise the limit.`;
}

/** The confirmation's numbers, formatted. */
export interface CostLines {
  rows: string;
  tokens: string;
  /** Undefined when the model has no published price — never guessed. */
  cost?: string;
  model: string;
}

export function costLines(estimate: TranslateEstimate): CostLines {
  const n = (value: number): string => value.toLocaleString("en-US");
  const lines: CostLines = {
    rows: `${n(estimate.rows)} ${estimate.rows === 1 ? "row" : "rows"}`,
    tokens: `~${n(estimate.approxTokensIn)} in / ~${n(estimate.approxTokensOut)} out`,
    model: estimate.model,
  };
  if (estimate.approxUsd !== undefined) lines.cost = formatUsd(estimate.approxUsd);
  return lines;
}

/**
 * A sub-cent estimate is the common case at Flash-Lite prices, and "$0.00"
 * reads as free rather than cheap — so anything under a cent says so.
 */
function formatUsd(usd: number): string {
  if (usd > 0 && usd < 0.01) return "< $0.01";
  return `$${usd.toFixed(2)}`;
}

/**
 * The result toast for a run whose breakdown the job API DID return — the only
 * place a user sees `translated` vs `skipped`, and what a run cost (F-70.7).
 *
 * `skipped` is not a failure: an empty source row, or a cell a human owns
 * (C5.1/F-70.5 — a re-run must never clobber a human edit), or — since resume
 * became the default — a row that was already translated. `failed` is
 * retryable and says so.
 */
export function resultSummary(result: TranslateColumnResult, columnName: string): string {
  const { translated, skipped, failed } = result;
  if (translated === 0 && skipped === 0 && failed === 0) {
    return `Nothing to translate into ${columnName} — the Original Script column is empty.`;
  }
  if (translated === 0 && failed === 0 && skipped > 0) {
    // The resume default's no-op: every row was already done. Saying "0
    // translated, 412 skipped" would read as a malfunction.
    return `${columnName} is already up to date — nothing needed translating. Use Re-translate to redo it.`;
  }
  const parts = [`${translated.toLocaleString("en-US")} translated`];
  if (skipped > 0) parts.push(`${skipped.toLocaleString("en-US")} skipped`);
  if (failed > 0) parts.push(`${failed.toLocaleString("en-US")} failed`);
  let text = `${columnName}: ${parts.join(", ")}${tokenSuffix(result)}.`;
  // A stopped run has rows left to do, so the retry advice takes precedence
  // over the failed-rows wording — running it again covers both.
  if (result.stopped !== undefined) {
    text +=
      result.stopped === "deadline"
        ? " The run hit its time limit before finishing — run it again to continue."
        : " The run stopped early — run it again to continue.";
  } else if (failed > 0) {
    text += " Run it again to retry the failed rows.";
  }
  return text;
}

/** " (1,234 tokens in / 567 out)" — or "" when the provider reported none. */
function tokenSuffix(result: TranslateColumnResult): string {
  const { tokensIn, tokensOut } = result;
  if (tokensIn === undefined && tokensOut === undefined) return "";
  const n = (value: number): string => value.toLocaleString("en-US");
  return ` (${n(tokensIn ?? 0)} tokens in / ${n(tokensOut ?? 0)} out)`;
}
